import { useState } from "react";
import { toast } from "sonner";
import { X, MapPin, Truck } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Opens from the Worker Dashboard "Quick Capture" FAB.
 * Creates a landing-pad (parking-lot) job with just an address + note,
 * then optionally uploads photos to that job.
 */
export default function QuickCaptureModal({ open, onClose, onCreated }) {
  const [address, setAddress] = useState("");
  const [unit, setUnit] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!address.trim()) {
      toast.error("Address is required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/parking-lot", {
        property_address: address.trim(),
        unit: unit.trim(),
        quick_note: note.trim(),
      });
      toast.success("Dropped to Parking Lot");
      setAddress("");
      setUnit("");
      setNote("");
      onCreated?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[130] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="quick-capture-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <form
        onSubmit={submit}
        className="bg-white w-full max-w-md border-2 border-[#0f172a] shadow-2xl"
      >
        <div className="bg-[#0f172a] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck weight="fill" size={20} className="text-amber-400" />
            <div className="font-display font-black uppercase tracking-widest text-sm">Drop To ClosureRadar</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="quick-capture-close"
            className="p-1 hover:bg-white/10"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-600">
            Drop an address here from the field. Office will promote it to a real work order later.
          </div>

          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <MapPin size={11} weight="fill" /> Property Address *
            </span>
            <input
              type="text"
              autoFocus
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St, City, State"
              data-testid="quick-capture-address"
              className="w-full h-11 mt-1 px-3 border-2 border-[#0f172a] text-sm"
            />
          </label>

          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Unit / Apt (optional)</span>
            <input
              type="text"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="A, #2, 101…"
              data-testid="quick-capture-unit"
              className="w-full h-11 mt-1 px-3 border-2 border-[#0f172a] text-sm"
            />
          </label>

          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Quick Note (optional)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="What did you see? — landlord will decide next steps."
              data-testid="quick-capture-note"
              className="w-full mt-1 px-3 py-2 border-2 border-[#0f172a] text-sm"
            />
          </label>
        </div>

        <div className="border-t-2 border-slate-200 px-4 py-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="quick-capture-cancel"
            className="h-10 px-4 text-[11px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 border-2 border-slate-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="quick-capture-submit"
            className="h-10 px-5 bg-amber-500 text-white text-[11px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40"
          >
            {busy ? "Dropping…" : "Drop to Parking Lot"}
          </button>
        </div>
      </form>
    </div>
  );
}
